import { Button, Card, Group, Stack, Text, Title } from '@mantine/core';
import { ExternalLink, Wrench } from 'lucide-react';
import { isOpenAiRepairConfigured } from '../../../ai-repair';
import type {
  RepairPreview,
  SessionAnalysisState,
  SessionRepairStatus,
} from '../types';
import {
  formatSessionDate,
  formatSessionEventsDuration,
} from '../utils/formatRepair';
import { SessionRepairStatusBadge } from './SessionRepairStatusBadge';

type SessionRepairDetailPanelProps = {
  sessionId: number;
  patientCode?: string | null;
  sessionDate?: string | Date | null;
  scenario?: string;
  repairStatus: SessionRepairStatus;
  analysis: SessionAnalysisState;
  effectiveEventsCount: number;
  effectiveEvents?: Parameters<typeof formatSessionEventsDuration>[0];
  repairPreview: RepairPreview | null;
  isAnalyzing: boolean;
  isRepairing: boolean;
  onAnalyze: () => void;
  onRepair: () => void;
  onOpenPreview: () => void;
};

export function SessionRepairDetailPanel({
  sessionId,
  patientCode,
  sessionDate,
  scenario,
  repairStatus,
  analysis,
  effectiveEventsCount,
  effectiveEvents,
  repairPreview,
  isAnalyzing,
  isRepairing,
  onAnalyze,
  onRepair,
  onOpenPreview,
}: SessionRepairDetailPanelProps) {
  const canRepair =
    analysis.status === 'needs_repair' && isOpenAiRepairConfigured();
  const detailLines =
    analysis.status === 'clean' || analysis.status === 'needs_repair'
      ? (analysis.findings ?? [])
      : analysis.status === 'failed'
        ? [analysis.message]
        : [];

  return (
    <Card withBorder>
      <Stack gap='md'>
        <Group justify='space-between' align='flex-start' wrap='wrap'>
          <div>
            <Group gap='xs' mb={4}>
              <Title order={4}>Sessione {sessionId}</Title>
              <SessionRepairStatusBadge status={repairStatus} />
            </Group>
            <Text size='sm' c='dimmed'>
              {patientCode ?? 'n/a'} · {scenario ?? 'n/a'} ·{' '}
              {formatSessionDate(sessionDate)}
            </Text>
            <Text size='sm' c='dimmed' mt={4}>
              Durata: {formatSessionEventsDuration(effectiveEvents)} · Eventi
              effettivi: {effectiveEventsCount}
            </Text>
          </div>
          <Group gap='xs'>
            <Button
              component='a'
              href={`/sessions/completed/${sessionId}`}
              target='_blank'
              variant='subtle'
              leftSection={<ExternalLink size={14} />}
            >
              Apri sessione
            </Button>
            <Button variant='light' loading={isAnalyzing} onClick={onAnalyze}>
              Analizza
            </Button>
            <Button
              leftSection={<Wrench size={14} />}
              loading={isRepairing}
              disabled={!canRepair}
              onClick={onRepair}
            >
              Ripara eventi
            </Button>
            {repairPreview && (
              <Button variant='subtle' onClick={onOpenPreview}>
                Vedi riparazioni
              </Button>
            )}
          </Group>
        </Group>

        {analysis.status === 'needs_repair' && (
          <Text size='sm' c='dimmed'>
            {analysis.duplicateCount} duplicati · {analysis.gapCount} gap
          </Text>
        )}

        {detailLines.length > 0 ? (
          <Stack gap={4}>
            {detailLines.map(line => (
              <Text key={line} size='sm'>
                {line}
              </Text>
            ))}
          </Stack>
        ) : (
          <Text size='sm' c='dimmed'>
            Esegui l&apos;analisi per verificare duplicati e gap sui dati
            originali.
          </Text>
        )}

        {!isOpenAiRepairConfigured() && analysis.status === 'needs_repair' && (
          <Text size='sm' c='orange'>
            OpenAI non configurato: imposta VITE_OPENAI_API_KEY in .env.local
            per abilitare la riparazione.
          </Text>
        )}
      </Stack>
    </Card>
  );
}
